import * as React from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  Line,
  XAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  RefreshCw,
  BookOpen,
  Loader2,
  Camera,
  Download,
  AlertTriangle,
} from "lucide-react";

import { cn } from "../../lib/utils";
import { AppLayout } from "../../components/ui-v2/layout/AppLayout";
import {
  CurrencyValue,
  PercentValue,
} from "../../components/ui-v2/value-display";
import { Heading, Text, MonoText } from "../../components/ui-v2/typography";
import { Card, CardContent } from "../../components/ui-v2/card";
import { Button } from "../../components/ui-v2/button";
import { Skeleton } from "../../components/ui-v2/skeleton";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "../../components/ui-v2/table";
import { StatCard } from "../../components/ui-v2/stat-card";
import { FilterPill, FilterPillGroup } from "../../components/ui-v2/filter-pill";
import { SegmentedControl, SegmentedControlOption } from "../../components/ui-v2/segmented-control";
import { ChartTooltipShell, ChartTooltipRow } from "../../components/ui-v2/chart-tooltip";
import {
  CHART_LINE,
  CHART_BENCHMARK_LINE,
  BENCHMARK_COLORS,
  DONUT_SLICE_COLORS,
  chartGradientStops,
  chartGridProps,
  chartAxisTickStyle,
  chartAxisLineProps,
  donutRingProps,
  getChartAnimationProps,
} from "../../components/ui-v2/chart-theme";
import { SnapshotDetailsDialog } from "../../components/ui-v2/SnapshotManagement";
import { ExportDialog } from "../../components/ui-v2/ExportDialog";
import { HoldingAgeInsight } from "../../components/ui-v2/HoldingAgeInsight";
import { usePortfolio } from "../../hooks/usePortfolio";
import { useBenchmarkComparison } from "../../hooks/useBenchmarkComparison";
import { useInsights } from "../../hooks/useInsights";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import { API_BASE_URL } from '../../lib/api';

/**
 * V2 Dashboard Page — Institutional Console layout (issue #235,
 * DESIGN-SPEC.md §5 "Dashboard").
 *
 * KPI band (4 sparkline stat-cards) -> Performance-vs-benchmarks (3fr,
 * 1M/3M/YTD/1Y/All range switch REAL — drives useBenchmarkComparison's
 * `days`) + Allocation/Cash-flow-YTD stack (1fr, rebalance-drift warning-tint
 * callout) -> Largest positions table (Top/Movers/Laggards toggle) + Daily
 * insights digest -> Snapshot history strip. Restyle only (#201 split
 * deferred) — all data fetching, hooks, and interactions from the
 * pre-redesign Dashboard are retained; see
 * `.claude/docs/issues/issue-235/sessions/` for the full feature-retention
 * audit and declared deviations (sparkline data source, rebalance-drift
 * heuristic; round-2 fixes for the range control, Cash-flow deep links, and
 * §6 motion compliance are documented in the round-2 session log).
 *
 * Connected to real portfolio API via usePortfolio/useBenchmarkComparison/
 * useInsights — no mock/placeholder data.
 */

const BENCHMARK_CONFIG = {
  sp500: { label: "S&P 500", shortLabel: "SPX" },
  nasdaq: { label: "NASDAQ", shortLabel: "NDX" },
  russell2000: { label: "Russell 2000", shortLabel: "R2K" },
  dax: { label: "DAX", shortLabel: "DAX" },
  stoxx600: { label: "STOXX 600", shortLabel: "STX" },
  msciWorld: { label: "MSCI World", shortLabel: "MSCI" },
  bitcoin: { label: "Bitcoin", shortLabel: "BTC" },
};

// Benchmark keys that get the dedicated `--chart-gold` slot of
// BENCHMARK_COLORS (spec §4.5: "chart-gold: BTC/real-estate series") instead
// of cycling through the two neutral colors by index. Round-2 fix
// (ui-design-expert review D-fix3): `BENCHMARK_COLORS[index % 3]` put BTC
// (index 6 of 7 keys) at `6 % 3 = 0` -> chart-neutral-1, losing its intended
// gold identity (Dashboard.dc.html:74 paints BTC's dashed line #8a6d3b, the
// dark chart-gold value). No other current BENCHMARK_CONFIG key represents
// metals/real-estate, but the set is kept (not a single bitcoin check) so a
// future metals/real-estate benchmark key gets the same treatment for free.
const GOLD_BENCHMARK_KEYS = new Set(["bitcoin"]);

/**
 * Resolves a benchmark series' line/swatch color: `chart-gold` for
 * GOLD_BENCHMARK_KEYS members, otherwise cycles the remaining benchmarks
 * (in BENCHMARK_CONFIG's declaration order, excluding gold-mapped keys)
 * through the two neutral colors so non-gold benchmarks still get visually
 * distinct dashed lines.
 *
 * Exported (named, not default) solely so `Dashboard.test.js` can unit-test
 * the D-fix3 color mapping directly — Recharts' `<Line stroke=…>`/tooltip
 * swatch colors aren't reliably queryable through RTL's DOM-only API without
 * simulating chart hover interactions, which would make the test far more
 * brittle than asserting the pure function's output.
 */
export function benchmarkColor(key) {
  if (GOLD_BENCHMARK_KEYS.has(key)) {
    return BENCHMARK_COLORS[BENCHMARK_COLORS.length - 1]; // --chart-gold
  }
  const neutralKeys = Object.keys(BENCHMARK_CONFIG).filter((k) => !GOLD_BENCHMARK_KEYS.has(k));
  const neutralIndex = neutralKeys.indexOf(key);
  const neutralColors = BENCHMARK_COLORS.slice(0, -1); // chart-neutral-1/2 only
  return neutralColors[neutralIndex % neutralColors.length];
}

const DEFAULT_VISIBLE_BENCHMARKS = { sp500: true, msciWorld: true, bitcoin: true };

// Chart time-range switch (§5 SegmentedControl 1M/3M/YTD/1Y/All). Round-2 fix
// (system-architect review F1): the control now REALLY drives
// `useBenchmarkComparison`'s `days` param (the hook already supports and
// refetches on it — `useBenchmarkComparison.js:11,30,141`) instead of being a
// no-op, mirroring `Analytics.jsx:105-107`'s `RANGE_DAYS`/`RANGE_LABELS`
// pattern. `RANGE_LABELS` drives the "N-day change" text in the Snapshot
// History strip so the label can never drift from the actual fetch window
// (the exact class of bug the #237 Analytics review caught and fixed).
// "YTD" has no fixed day-count — `ytdDays()` computes it from the current
// date so it stays correct year-round.
const RANGE_OPTIONS = ["1M", "3M", "YTD", "1Y", "All"];
function ytdDays() {
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  return Math.max(1, Math.round((now - startOfYear) / (1000 * 60 * 60 * 24)));
}
const RANGE_DAYS = { "1M": 30, "3M": 90, YTD: ytdDays(), "1Y": 365, All: 3650 };
const RANGE_LABELS = { "1M": "30-day", "3M": "90-day", YTD: "YTD", "1Y": "365-day", All: "All-time" };

const CATEGORY_LEGEND = [
  { key: "stocks", label: "Equities", href: "/holdings/investments" },
  { key: "crypto", label: "Crypto", href: "/holdings/investments" },
  { key: "cash", label: "Cash", href: "/holdings/cash" },
  { key: "realEstate", label: "Real estate", href: "/holdings/real-estate" },
];

// Placeholder rebalance target bands (D4 — no backend target-allocation
// feature exists yet; this is a client-side heuristic only, not a real
// signal). Tracked as a follow-up: a real "target allocation" setting should
// replace these hardcoded bands.
const REBALANCE_TARGET_BANDS = {
  crypto: 15,
};
const REBALANCE_DRIFT_THRESHOLD_PTS = 2;

function formatChartDate(dateString) {
  const date = new Date(`${dateString}T00:00:00`);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatTooltipDate(dateString) {
  const date = new Date(`${dateString}T00:00:00`);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/**
 * Hook to fetch financial summary data (deposits/withdrawals/dividends/realized gains).
 * Unchanged from the pre-redesign Dashboard.jsx — powers the "Cash flow YTD" panel.
 */
function useFinancialSummary() {
  const [data, setData] = React.useState({
    totalDeposits: 0,
    totalWithdrawals: 0,
    totalDividends: 0,
    totalRealizedGains: 0,
    depositCount: 0,
    withdrawalCount: 0,
    dividendCount: 0,
    closedPositionCount: 0,
  });
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchFinancialData = async () => {
      try {
        setLoading(true);
        const [contributionsRes, closedPositionsRes, dividendsRes, withdrawalsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/contributions`, { withCredentials: true }).catch(() => ({ data: { contributions: [] } })),
          axios.get(`${API_BASE_URL}/transactions/closed-positions`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API_BASE_URL}/transactions/dividends`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API_BASE_URL}/transactions/withdrawals`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);

        const contributions = contributionsRes.data?.contributions || [];
        let totalDeposits = 0;
        let contributionWithdrawals = 0;
        let depositCount = 0;
        let contributionWithdrawalCount = 0;

        contributions.forEach((c) => {
          const amount = parseFloat(c.amountEUR) || parseFloat(c.amount) || 0;
          if (c.type === 'deposit') {
            totalDeposits += amount;
            depositCount++;
          } else if (c.type === 'withdrawal') {
            contributionWithdrawals += amount;
            contributionWithdrawalCount++;
          }
        });

        const dividends = dividendsRes.data || [];
        const totalDividends = dividends.reduce((sum, d) => {
          if (d.totalDividendAmountEUR) {
            return sum + (parseFloat(d.totalDividendAmountEUR) || 0);
          }
          if (d.statementType === "ANNUAL") {
            return sum + (parseFloat(d.totalAmount) || 0);
          }
          return sum + (parseFloat(d.dividendPerShare) || 0) * (parseFloat(d.sharesOwned) || 0);
        }, 0);

        const closedPositions = closedPositionsRes.data || [];
        const totalRealizedGains = closedPositions.reduce((sum, p) => {
          return sum + (parseFloat(p.realizedGainEUR) || parseFloat(p.realizedGain) || 0);
        }, 0);

        const transactionWithdrawals = withdrawalsRes.data || [];
        const totalTransactionWithdrawals = transactionWithdrawals.reduce((sum, w) => {
          return sum + (parseFloat(w.amountEUR) || parseFloat(w.amount) || 0);
        }, 0);

        setData({
          totalDeposits,
          totalWithdrawals: contributionWithdrawals + totalTransactionWithdrawals,
          totalDividends,
          totalRealizedGains,
          depositCount,
          withdrawalCount: contributionWithdrawalCount + transactionWithdrawals.length,
          dividendCount: dividends.length,
          closedPositionCount: closedPositions.length,
        });
      } catch (err) {
        console.error("Error fetching financial data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchFinancialData();
  }, []);

  return { data, loading };
}

/**
 * Hook to fetch snapshot history — powers both the KPI sparklines (D2: derived
 * client-side from the same history, no new endpoint) and the Snapshot
 * History strip. Unchanged endpoint from the pre-redesign SnapshotManagement.
 */
function useSnapshotHistory() {
  const [snapshots, setSnapshots] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [creating, setCreating] = React.useState(false);

  const fetchSnapshots = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/snapshots`, { withCredentials: true });
      setSnapshots(response.data.snapshots || []);
    } catch (err) {
      console.error("Error fetching snapshots:", err);
      setError(err.response?.data?.error || "Failed to load snapshots");
    } finally {
      setLoading(false);
    }
  }, []);

  const createSnapshot = React.useCallback(async () => {
    try {
      setCreating(true);
      setError(null);
      await axios.post(`${API_BASE_URL}/snapshots/trigger`, {}, { withCredentials: true });
      await fetchSnapshots();
    } catch (err) {
      console.error("Error creating snapshot:", err);
      setError(err.response?.data?.error || "Failed to create snapshot");
    } finally {
      setCreating(false);
    }
  }, [fetchSnapshots]);

  React.useEffect(() => {
    fetchSnapshots();
  }, [fetchSnapshots]);

  // Listen for the global "Take Snapshot" action (command palette / other
  // triggers) — preserved from the pre-redesign SnapshotManagement.
  React.useEffect(() => {
    const handleTakeSnapshot = () => createSnapshot();
    window.addEventListener("v2:take-snapshot", handleTakeSnapshot);
    return () => window.removeEventListener("v2:take-snapshot", handleTakeSnapshot);
  }, [createSnapshot]);

  const chartData = React.useMemo(() => {
    if (!snapshots || snapshots.length === 0) return [];
    return [...snapshots]
      .map((s) => ({ date: s.date, value: s.netWorth || s.totalValue || 0, snapshot: s }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [snapshots]);

  return { snapshots, chartData, loading, error, creating, createSnapshot, refresh: fetchSnapshots };
}

/**
 * Builds a 96x34 sparkline series (D2) from ordered {date, value} points,
 * omitted entirely by StatCard when fewer than 2 points exist.
 */
function buildSparkline(chartData, key = "value") {
  if (!chartData || chartData.length < 2) return null;
  return chartData.slice(-24).map((point) => ({ value: point[key] ?? point.value }));
}

function PerfBenchmarksTooltip({ active, payload, label, visibleBenchmarks }) {
  const data = payload?.[0]?.payload;
  if (!active || !data) return null;
  return (
    <ChartTooltipShell active={active} payload={payload} label={formatTooltipDate(data.date)} allowEmptyPayload>
      <ChartTooltipRow label="Portfolio" value={<CurrencyValue value={data.value} size="xs" />} swatchColor="var(--accent, var(--color-primary-500))" />
      {Object.entries(BENCHMARK_CONFIG).map(([key, config]) => {
        if (!visibleBenchmarks[key] || data[key] == null) return null;
        return (
          <ChartTooltipRow
            key={key}
            label={config.label}
            value={<CurrencyValue value={data[key]} size="xs" />}
            swatchColor={benchmarkColor(key)}
          />
        );
      })}
    </ChartTooltipShell>
  );
}

/**
 * Performance vs benchmarks panel (3fr column). Rebuilds the pre-redesign
 * BenchmarkComparisonChart's data merge/normalize logic with chart-theme.js
 * tokens + FilterPill benchmark toggle (spec §4.5/§5).
 *
 * Round-2 fix (system-architect review F1): `range`/`onRangeChange` are now
 * lifted to `Dashboard()` (owner of `useBenchmarkComparison`) instead of
 * being local dead state — the SegmentedControl really drives the fetched
 * window (see `RANGE_DAYS` above), it no longer just changes its own visual
 * selection with no effect on the chart.
 */
function PerformanceVsBenchmarksPanel({ benchmarkData, range, onRangeChange }) {
  const { portfolioData, benchmarks, comparison, loading, error } = benchmarkData;
  const [visibleBenchmarks, setVisibleBenchmarks] = React.useState(DEFAULT_VISIBLE_BENCHMARKS);
  const prefersReducedMotion = usePrefersReducedMotion();

  const toggleBenchmark = (key) => {
    setVisibleBenchmarks((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const chartData = React.useMemo(() => {
    if (!portfolioData || portfolioData.length === 0) return [];
    const dataMap = new Map();
    portfolioData.forEach((point) => {
      dataMap.set(point.date, { date: point.date, value: point.value });
    });
    Object.entries(benchmarks).forEach(([key, arr]) => {
      if (!Array.isArray(arr)) return;
      arr.forEach((point) => {
        const existing = dataMap.get(point.date) || { date: point.date };
        existing[key] = point.value;
        dataMap.set(point.date, existing);
      });
    });
    return Array.from(dataMap.values())
      .filter((d) => d.value != null)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [portfolioData, benchmarks]);

  const benchmarkPerformances = React.useMemo(() => {
    const performances = {};
    Object.entries(BENCHMARK_CONFIG).forEach(([key, config]) => {
      const series = benchmarks[key];
      if (!series || series.length === 0) return;
      const start = series[0]?.value;
      const end = series[series.length - 1]?.value;
      if (start > 0) {
        performances[key] = { ...config, return: ((end - start) / start) * 100 };
      }
    });
    return performances;
  }, [benchmarks]);

  const portfolioReturn = comparison?.portfolioReturn || 0;

  return (
    <Card className="rounded-lg p-4 sm:p-[18px]">
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <div className="flex flex-wrap items-center gap-3">
          <Heading level={5} className="text-[13.5px] font-semibold">
            Performance vs benchmarks
          </Heading>
          <FilterPillGroup aria-label="Toggle benchmark visibility">
            <FilterPill active className="font-mono">
              PORT <PercentValue value={portfolioReturn} size="xs" showSign showTrend={false} className="ml-0.5" />
            </FilterPill>
            {Object.entries(benchmarkPerformances).map(([key, perf]) => (
              <FilterPill
                key={key}
                active={!!visibleBenchmarks[key]}
                onClick={() => toggleBenchmark(key)}
                aria-label={`Toggle ${perf.label} benchmark`}
                className="font-mono"
              >
                {perf.shortLabel} <PercentValue value={perf.return} size="xs" showSign showTrend={false} className="ml-0.5" />
              </FilterPill>
            ))}
          </FilterPillGroup>
        </div>
        <SegmentedControl value={range} onValueChange={onRangeChange} aria-label="Chart time range">
          {RANGE_OPTIONS.map((opt) => (
            <SegmentedControlOption key={opt} value={opt}>
              {opt}
            </SegmentedControlOption>
          ))}
        </SegmentedControl>
      </div>

      <div className="mt-3.5 h-[250px]">
        {loading ? (
          <Skeleton className="h-full w-full rounded-md" />
        ) : error ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <Text variant="secondary" size="sm">Failed to load benchmark data</Text>
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-1 text-center">
            <Text variant="secondary" size="sm">No comparison data yet</Text>
            <Text variant="muted" size="xs">Create snapshots over time to compare performance.</Text>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
              <defs>
                <linearGradient id="dashboardPerfGradient" x1="0" y1="0" x2="0" y2="1">
                  {chartGradientStops.map((stop) => (
                    <stop key={stop.offset} offset={stop.offset} stopColor={stop.stopColor} stopOpacity={stop.stopOpacity} />
                  ))}
                </linearGradient>
              </defs>
              <CartesianGrid {...chartGridProps} />
              <XAxis
                dataKey="date"
                tickFormatter={formatChartDate}
                tick={{ style: chartAxisTickStyle }}
                interval="preserveStartEnd"
                {...chartAxisLineProps}
              />
              <RechartsTooltip
                content={(props) => <PerfBenchmarksTooltip {...props} visibleBenchmarks={visibleBenchmarks} />}
              />
              {Object.entries(BENCHMARK_CONFIG).map(([key, config]) => {
                if (!visibleBenchmarks[key] || !benchmarks[key]?.length) return null;
                return (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    name={config.label}
                    stroke={benchmarkColor(key)}
                    {...CHART_BENCHMARK_LINE}
                    {...getChartAnimationProps(prefersReducedMotion)}
                  />
                );
              })}
              <Area
                type="monotone"
                dataKey="value"
                name="Portfolio"
                fill="url(#dashboardPerfGradient)"
                fillOpacity={1}
                {...CHART_LINE}
                {...getChartAnimationProps(prefersReducedMotion)}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </Card>
  );
}

/**
 * Allocation donut + rebalance-drift callout (D4). Replaces the old
 * CategoryBreakdown progress-bar list with a donut + legend per mockup, same
 * 4 categories/hrefs/percentages.
 */
function AllocationPanel({ summary, loading }) {
  const categories = React.useMemo(() => {
    const breakdown = summary?.breakdownEUR || {};
    return CATEGORY_LEGEND.map((cat) => ({ ...cat, value: breakdown[cat.key] || 0 }));
  }, [summary]);

  const total = categories.reduce((sum, c) => sum + c.value, 0);
  const sliceData = categories
    .map((c, index) => ({
      ...c,
      percent: total > 0 ? (c.value / total) * 100 : 0,
      color: DONUT_SLICE_COLORS[index % DONUT_SLICE_COLORS.length],
    }))
    .filter((c) => c.value > 0);

  const drift = React.useMemo(() => {
    for (const [key, target] of Object.entries(REBALANCE_TARGET_BANDS)) {
      const cat = categories.find((c) => c.key === key);
      if (!cat || total <= 0) continue;
      const sharePct = (cat.value / total) * 100;
      const overshootPts = sharePct - target;
      if (overshootPts > REBALANCE_DRIFT_THRESHOLD_PTS) {
        const rebalanceAmount = (overshootPts / 100) * total;
        return { label: cat.label, overshootPts, rebalanceAmount };
      }
    }
    return null;
  }, [categories, total]);

  return (
    <Card className="rounded-lg p-4 sm:p-[18px]">
      <div className="flex items-center justify-between">
        <Heading level={5} className="text-[13.5px] font-semibold">Allocation</Heading>
        <Link to="/holdings/investments" className="text-[11.5px] font-medium hover:underline">
          Detail →
        </Link>
      </div>
      {loading ? (
        <div className="mt-3 flex items-center gap-3.5">
          <Skeleton className="h-[88px] w-[88px] shrink-0 rounded-full" />
          <div className="flex-1 space-y-1.5">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-3.5 w-full" />
            ))}
          </div>
        </div>
      ) : sliceData.length === 0 ? (
        <Text variant="secondary" size="sm" className="mt-3 text-center py-4">
          No allocation data yet
        </Text>
      ) : (
        <>
          <div className="mt-3 flex items-center gap-3.5">
            <div className="h-[88px] w-[88px] shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  {/* Round-2 fix (ui-design-expert review D-fix2): chart-theme.js's
                      24px ring width is expressed in the mockup's 140-unit
                      viewBox coordinate space (donutArc(70,70,62,38,…),
                      chart-theme.js:118-124's own comment). Recharts'
                      `outerRadius` here is REAL display pixels (this <PieChart>
                      has no separate viewBox — it fills the 88x88 container
                      directly), so the 140-space radius must be scaled down by
                      88/140 ≈ 0.6286 to match: 62 * 0.6286 ≈ 39 (was 44, which
                      rendered a visibly thicker/larger ring than the mockup). */}
                  <Pie data={sliceData} dataKey="value" nameKey="label" {...donutRingProps({ outerRadius: 39 })} isAnimationActive={false}>
                    {sliceData.map((slice) => (
                      <Cell key={slice.key} fill={slice.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-1 flex-col gap-1.5 text-[11.5px]">
              {sliceData.map((slice) => (
                <Link
                  key={slice.key}
                  to={slice.href}
                  className="flex items-center gap-1.5 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <span className="h-[7px] w-[7px] shrink-0 rounded-sm" style={{ backgroundColor: slice.color }} aria-hidden="true" />
                  <span className="flex-1 text-[var(--text-muted,var(--color-text-tertiary))]">{slice.label}</span>
                  <MonoText size="xs">{slice.percent.toFixed(1)}%</MonoText>
                </Link>
              ))}
            </div>
          </div>
          {drift && (
            <div
              role="status"
              className="mt-3 rounded-md px-2.5 py-2 text-[11.5px] leading-[1.45] text-[var(--warning-tint-text,var(--color-warning-500))]"
              style={{
                // Round-2 fix (ui-design-expert review D-fix1): the hardcoded
                // rgba(217,161,59,…) literal below was the DARK --warning
                // (#d9a13b) baked into both themes — it never theme-flipped,
                // so console-light rendered a dark-amber wash instead of the
                // light mockup's ochre (Dashboard Light.dc.html:120 uses the
                // light --warning #a06f18). `color-mix` derives the same
                // translucency the mockups use (bg 7-8%, border 25%) from
                // whichever theme's --warning is active, so both themes flip
                // correctly with zero new CSS tokens (additive-safe — no
                // theme file edits).
                backgroundColor: "color-mix(in srgb, var(--warning, var(--color-warning-500)) 8%, transparent)",
                border: "1px solid color-mix(in srgb, var(--warning, var(--color-warning-500)) 25%, transparent)",
              }}
            >
              <AlertTriangle className="mr-1 inline h-3 w-3 align-text-bottom" aria-hidden="true" />
              {drift.label} +{drift.overshootPts.toFixed(1)}pt over target — rebalance{" "}
              <CurrencyValue value={drift.rebalanceAmount} size="xs" className="font-inherit text-inherit" compact />
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/**
 * Cash flow YTD panel — same useFinancialSummary metrics, restyled as a list
 * per mockup.
 *
 * Round-2 fix (system-architect review F2): the old `FinancialSummaryCards`
 * gave Dividends and Realized Gains their OWN deep links
 * (`/transactions/dividends`, `/transactions/realized-gains` — both live
 * routes, `App.js:117` `/transactions/:type`), distinct from Deposits/
 * Withdrawals' `/contributions`. Round-1 collapsed all four rows under a
 * single panel-level "Detail →" to `/contributions`, silently dropping the
 * two `/transactions/*` destinations. Each row is now individually linked to
 * its correct destination (keeping the compact list look — no functionality
 * lost, no panel-level "Detail →" needed since every row is now a link).
 */
function CashFlowYtdPanel() {
  const { data, loading } = useFinancialSummary();
  const netRealizedGains = data.totalRealizedGains;

  const rows = [
    { label: "Deposits", value: data.totalDeposits, href: "/contributions" },
    { label: "Withdrawals", value: -data.totalWithdrawals, href: "/contributions" },
    { label: "Dividends", value: data.totalDividends, href: "/transactions/dividends" },
  ];

  return (
    <Card className="flex flex-1 flex-col rounded-lg p-4 sm:p-[18px]">
      <Heading level={5} className="text-[13.5px] font-semibold">Cash flow YTD</Heading>
      {loading ? (
        <div className="mt-2.5 space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-4 w-full" />
          ))}
        </div>
      ) : (
        <div className="mt-2.5 flex flex-1 flex-col gap-1.5 text-xs">
          {rows.map((row) => (
            <Link
              key={row.label}
              to={row.href}
              className="flex items-center justify-between rounded-sm transition-colors duration-[120ms] ease-out hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <Text size="xs" className="text-[var(--text-muted,var(--color-text-tertiary))]">{row.label}</Text>
              <MonoText size="xs">
                {row.value < 0 ? "−" : ""}
                {new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Math.abs(row.value))}
              </MonoText>
            </Link>
          ))}
          <Link
            to="/transactions/realized-gains"
            className="flex items-center justify-between rounded-sm border-t border-hairline pt-[7px] transition-colors duration-[120ms] ease-out hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <Text size="xs" className="font-medium text-[var(--text-muted,var(--color-text-tertiary))]">Realised gains</Text>
            <MonoText size="xs" className={cn(netRealizedGains >= 0 ? "text-success" : "text-destructive")}>
              {netRealizedGains >= 0 ? "+" : "−"}
              {new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Math.abs(netRealizedGains))}
            </MonoText>
          </Link>
        </div>
      )}
    </Card>
  );
}

/**
 * Largest positions table with Top/Movers/Laggards toggle — consolidates the
 * old TopPerformers + WorstPerformers cards into one table per spec §5.
 */
function LargestPositionsTable({ holdings, loading }) {
  const [mode, setMode] = React.useState("top");

  const investments = React.useMemo(
    () => (holdings || []).filter((h) => !h.isLiability && h.totalValueEUR !== undefined),
    [holdings]
  );

  const totalValue = React.useMemo(
    () => investments.reduce((sum, h) => sum + (h.totalValueEUR || 0), 0),
    [investments]
  );

  const rows = React.useMemo(() => {
    const withWeight = investments.map((h) => ({
      ...h,
      weight: totalValue > 0 ? ((h.totalValueEUR || 0) / totalValue) * 100 : 0,
    }));
    if (mode === "movers") {
      return [...withWeight].sort((a, b) => Math.abs(b.gainPercent || 0) - Math.abs(a.gainPercent || 0)).slice(0, 10);
    }
    if (mode === "laggards") {
      return [...withWeight].sort((a, b) => (a.gainPercent || 0) - (b.gainPercent || 0)).slice(0, 10);
    }
    return [...withWeight].sort((a, b) => (b.totalValueEUR || 0) - (a.totalValueEUR || 0)).slice(0, 10);
  }, [investments, mode, totalValue]);

  return (
    <Card className="rounded-lg p-0">
      <div className="flex flex-wrap items-center gap-3 border-b border-hairline px-4 py-3 sm:px-[18px]">
        <Heading level={5} className="text-[13.5px] font-semibold">Largest positions</Heading>
        <MonoText size="xs" className="text-[var(--text-faint,var(--color-text-disabled))]">
          {investments.length} position{investments.length !== 1 ? "s" : ""} · by weight
        </MonoText>
        <FilterPillGroup className="ml-auto" aria-label="Position ranking mode">
          <FilterPill active={mode === "top"} onClick={() => setMode("top")}>Top</FilterPill>
          <FilterPill active={mode === "movers"} onClick={() => setMode("movers")}>Movers</FilterPill>
          <FilterPill active={mode === "laggards"} onClick={() => setMode("laggards")}>Laggards</FilterPill>
        </FilterPillGroup>
        <Link to="/holdings/investments" className="text-[11.5px] font-medium hover:underline">
          All holdings →
        </Link>
      </div>
      {loading ? (
        <div className="space-y-2 p-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <Text variant="secondary" size="sm" className="py-8 text-center">
          No investment data available
        </Text>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Asset</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead className="text-right">Value</TableHead>
              <TableHead className="text-right">Weight</TableHead>
              <TableHead className="text-right">Total gain</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((holding) => {
              const isPositive = (holding.gainPercent || 0) >= 0;
              return (
                <TableRow key={holding.holdingId || holding.id}>
                  <TableCell>
                    <span className="font-medium">{holding.name || holding.assetName}</span>{" "}
                    <MonoText size="xs" className="ml-1.5 text-[var(--text-faint,var(--color-text-disabled))]">
                      {holding.ticker || "-"}
                    </MonoText>
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-[var(--text-muted,var(--color-text-tertiary))]">
                    <CurrencyValue
                      value={holding.currentPrice ?? holding.currentPriceEUR}
                      currency={holding.tradingCurrency || holding.currency || "EUR"}
                      size="xs"
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <CurrencyValue value={holding.totalValueEUR} size="xs" />
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-[var(--text-muted,var(--color-text-tertiary))]">
                    {holding.weight.toFixed(1)}%
                  </TableCell>
                  <TableCell className={cn("text-right font-mono tabular-nums", isPositive ? "text-success" : "text-destructive")}>
                    <PercentValue value={holding.gainPercent || 0} size="xs" showSign showTrend={false} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

/** Daily insights digest (1fr) — wired to the real useInsights() hook. */
function DailyInsightsPanel() {
  const { insights, loading, unreadCount } = useInsights({ autoRefreshInterval: 0 });
  const recent = insights.slice(0, 4);

  return (
    <Card className="flex flex-col rounded-lg p-4 sm:p-[18px]">
      <div className="flex items-center justify-between">
        <Heading level={5} className="text-[13.5px] font-semibold">Daily insights</Heading>
        {unreadCount > 0 && (
          <MonoText size="xs" className="text-primary">{unreadCount} new</MonoText>
        )}
      </div>
      <div className="mt-3 flex flex-1 flex-col gap-3">
        {loading ? (
          [1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)
        ) : recent.length === 0 ? (
          <Text variant="secondary" size="sm" className="py-4 text-center">
            No insights yet
          </Text>
        ) : (
          recent.map((insight) => (
            <div
              key={insight.insightId}
              className={cn(
                "border-l-2 pl-3",
                insight.read ? "border-hairline" : "border-primary"
              )}
            >
              <Text size="sm" className={cn("font-semibold leading-tight", insight.read && "font-medium text-[var(--text-muted,var(--color-text-tertiary))]")}>
                {insight.title}
              </Text>
              <Text size="xs" className="mt-0.5 leading-[1.5] text-[var(--text-muted,var(--color-text-tertiary))]">
                {insight.description}
              </Text>
            </div>
          ))
        )}
      </div>
      <Link
        to="/insights"
        className="mt-3.5 block border-t border-hairline pt-3 text-[11.5px] font-medium hover:underline"
      >
        All insights →
      </Link>
    </Card>
  );
}

/** Snapshot history strip — full-width row replacing the old SnapshotManagement card. */
function SnapshotHistoryStrip({ snapshotHistory, range }) {
  const { snapshots, chartData, loading, error, creating, createSnapshot } = snapshotHistory;
  const [selectedSnapshot, setSelectedSnapshot] = React.useState(null);
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Round-2 fix (system-architect review F1 ripple): the change label now
  // tracks the SAME `range` selected in the Performance-vs-benchmarks panel
  // instead of a hardcoded "90-day change" that lied for any other window
  // (the exact defect the #237 Analytics review caught — see RANGE_LABELS
  // above). `chartData` here holds the FULL snapshot history (this strip's
  // own `/snapshots` fetch, unbounded), so the range window is applied by
  // filtering to points within the last `RANGE_DAYS[range]` days rather than
  // re-fetching — Snapshot History intentionally keeps showing full history
  // in the sparkline itself; only the change/label figure is range-scoped.
  const rangeLabel = RANGE_LABELS[range] || RANGE_LABELS["3M"];
  const rangeChange = React.useMemo(() => {
    if (chartData.length < 2) return null;
    const days = RANGE_DAYS[range] ?? RANGE_DAYS["3M"];
    const newest = chartData[chartData.length - 1];
    const cutoff = new Date(newest.date);
    cutoff.setDate(cutoff.getDate() - days);
    const windowed = chartData.filter((point) => new Date(point.date) >= cutoff);
    const oldest = windowed.length > 0 ? windowed[0] : chartData[0];
    if (!oldest || oldest.value === 0) return null;
    return { delta: newest.value - oldest.value, pct: ((newest.value - oldest.value) / oldest.value) * 100 };
  }, [chartData, range]);

  const cadence = React.useMemo(() => {
    if (snapshots.length < 2) return null;
    const sorted = [...snapshots].map((s) => new Date(s.date)).sort((a, b) => a - b);
    const spanDays = (sorted[sorted.length - 1] - sorted[0]) / (1000 * 60 * 60 * 24);
    return spanDays > 0 ? spanDays / (sorted.length - 1) : null;
  }, [snapshots]);

  const lastDate = snapshots.length > 0 ? snapshots[0].date : null;

  return (
    <Card className="rounded-lg p-0">
      <div className="flex flex-wrap items-center gap-3 border-b border-hairline px-4 py-3 sm:px-[18px]">
        <Heading level={5} className="text-[13.5px] font-semibold">Snapshot history</Heading>
        <MonoText size="xs" className="text-[var(--text-faint,var(--color-text-disabled))]">
          {snapshots.length} snapshot{snapshots.length !== 1 ? "s" : ""}
          {lastDate && ` · last: ${formatChartDate(lastDate)}`}
        </MonoText>
        <Button variant="outline" size="sm" className="ml-auto" onClick={createSnapshot} disabled={creating}>
          {creating ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
          ) : (
            <Camera className="mr-1.5 h-3.5 w-3.5" />
          )}
          {creating ? "Creating…" : "Snapshot"}
        </Button>
        <Link to="/snapshots" className="text-[11.5px] font-medium hover:underline">
          All snapshots →
        </Link>
      </div>
      <div className="flex items-center gap-6 px-4 py-3.5 sm:px-[18px]">
        {loading ? (
          <Skeleton className="h-16 flex-1" />
        ) : error ? (
          <Text variant="secondary" size="sm">{error}</Text>
        ) : chartData.length === 0 ? (
          <Text variant="secondary" size="sm">No snapshots yet — create your first one to track history.</Text>
        ) : (
          <>
            <div className="h-16 min-w-0 flex-1">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={chartData}
                  margin={{ top: 2, right: 2, left: 2, bottom: 2 }}
                  onClick={(e) => {
                    if (e?.activePayload?.[0]) {
                      setSelectedSnapshot(e.activePayload[0].payload.snapshot);
                      setDetailsOpen(true);
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <defs>
                    <linearGradient id="snapshotStripGradient" x1="0" y1="0" x2="0" y2="1">
                      {chartGradientStops.map((stop) => (
                        <stop key={stop.offset} offset={stop.offset} stopColor={stop.stopColor} stopOpacity={stop.stopOpacity} />
                      ))}
                    </linearGradient>
                  </defs>
                  <RechartsTooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.[0]) return null;
                      const point = payload[0].payload;
                      return (
                        <ChartTooltipShell active={active} payload={payload} label={formatTooltipDate(point.date)}>
                          <ChartTooltipRow label="Net worth" value={<CurrencyValue value={point.value} size="xs" />} />
                        </ChartTooltipShell>
                      );
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    fill="url(#snapshotStripGradient)"
                    fillOpacity={1}
                    {...CHART_LINE}
                    {...getChartAnimationProps(prefersReducedMotion)}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex shrink-0 gap-8">
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-[var(--text-faint,var(--color-text-disabled))]">
                  {rangeLabel} change
                </div>
                {rangeChange ? (
                  <div className={cn("mt-[3px] font-mono text-[15px] font-semibold", rangeChange.delta >= 0 ? "text-success" : "text-destructive")}>
                    {rangeChange.delta >= 0 ? "+" : "−"}
                    {new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Math.abs(rangeChange.delta))}
                    {" · "}
                    {rangeChange.pct >= 0 ? "+" : ""}
                    {rangeChange.pct.toFixed(2)}%
                  </div>
                ) : (
                  <div className="mt-[3px] font-mono text-[15px] font-semibold text-[var(--text-muted,var(--color-text-tertiary))]">—</div>
                )}
              </div>
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.07em] text-[var(--text-faint,var(--color-text-disabled))]">
                  Cadence
                </div>
                <div className="mt-[3px] font-mono text-[15px] font-semibold">
                  {cadence ? `every ~${Math.round(cadence)} day${Math.round(cadence) !== 1 ? "s" : ""}` : "—"}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <SnapshotDetailsDialog snapshot={selectedSnapshot} open={detailsOpen} onOpenChange={setDetailsOpen} />
    </Card>
  );
}

export function Dashboard() {
  const {
    holdings,
    summary,
    loading,
    error,
    refresh,
    lastUpdated,
  } = usePortfolio();

  // Chart time-range switch (round-2 fix, system-architect review F1): owned
  // here (not inside PerformanceVsBenchmarksPanel) because it drives
  // useBenchmarkComparison's `days` argument directly — a real refetch, not a
  // presentational-only control. Pre-selects "3M" to match the mockup.
  const [range, setRange] = React.useState("3M");
  const benchmarkData = useBenchmarkComparison(
    ["stocks", "crypto", "preciousMetals"],
    RANGE_DAYS[range]
  );

  const snapshotHistory = useSnapshotHistory();

  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [exportDialogOpen, setExportDialogOpen] = React.useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  const totalGain = React.useMemo(() => {
    if (!holdings || holdings.length === 0) return 0;
    return holdings.filter((h) => !h.isLiability).reduce((sum, h) => sum + (h.gainEUR || 0), 0);
  }, [holdings]);

  const totalCost = React.useMemo(() => {
    if (!holdings || holdings.length === 0) return 0;
    return holdings
      .filter((h) => !h.isLiability)
      .reduce((sum, h) => {
        const units = parseFloat(h.units) || 0;
        const price = parseFloat(h.purchasePrice) || 0;
        return sum + units * price;
      }, 0);
  }, [holdings]);

  const totalGainPercent = totalCost > 0 ? (totalGain / totalCost) * 100 : 0;

  // KPI band (D2: sparklines derived from the same snapshot history used by
  // the Snapshot History strip; omitted by StatCard when < 2 points).
  const netWorthSpark = buildSparkline(snapshotHistory.chartData);
  const assetsSpark = React.useMemo(
    () => buildSparkline(snapshotHistory.snapshots.map((s) => ({ date: s.date, value: s.totalAssets || 0 })).sort((a, b) => new Date(a.date) - new Date(b.date))),
    [snapshotHistory.snapshots]
  );
  const liabilitiesSpark = React.useMemo(
    () => buildSparkline(snapshotHistory.snapshots.map((s) => ({ date: s.date, value: s.totalLiabilities || 0 })).sort((a, b) => new Date(a.date) - new Date(b.date))),
    [snapshotHistory.snapshots]
  );

  const liabilitiesEUR = summary?.totalLiabilitiesEUR || 0;
  const assetsEUR = summary?.totalAssetsEUR || 0;
  const liabilitiesOfAssetsPct = assetsEUR > 0 ? (liabilitiesEUR / assetsEUR) * 100 : 0;

  const kpis = [
    {
      label: "Net worth",
      value: <CurrencyValue value={summary?.netWorthEUR || 0} size="2xl" className="!text-[22px] !font-semibold" />,
      delta: `${totalGainPercent >= 0 ? "+" : ""}${totalGainPercent.toFixed(2)}% all-time`,
      deltaTone: totalGainPercent >= 0 ? "positive" : "negative",
      sparklineData: netWorthSpark,
    },
    {
      label: "Assets",
      value: <CurrencyValue value={assetsEUR} size="2xl" className="!text-[22px] !font-semibold" />,
      delta: "Total assets",
      deltaTone: "muted",
      sparklineData: assetsSpark,
    },
    {
      // D7 (round-2, added per system-architect review non-blocking note):
      // the old Dashboard colorized Liabilities red-if-debt/green-if-zero;
      // this KPI is intentionally neutral (`--text`/`deltaTone: "muted"`)
      // per StatCard's spec contract (§4.2: "Value color: --text unless the
      // metric itself is a signed P&L") and the mockup, which paints
      // Liabilities' value/sub-line neutral (Dashboard.dc.html:38-39, KPI
      // #3). A deliberate spec-conformance choice, not an oversight.
      label: "Liabilities",
      value: <CurrencyValue value={liabilitiesEUR} size="2xl" className="!text-[22px] !font-semibold" />,
      delta: `${liabilitiesOfAssetsPct.toFixed(1)}% of assets`,
      deltaTone: "muted",
      sparklineData: liabilitiesSpark,
      sparklineTone: "muted",
    },
    {
      label: "Unrealised P&L",
      value: (
        <CurrencyValue
          value={totalGain}
          size="2xl"
          showSign
          className={cn("!text-[22px] !font-semibold", totalGain >= 0 ? "text-success" : "text-destructive")}
        />
      ),
      delta: `${totalGainPercent >= 0 ? "+" : ""}${totalGainPercent.toFixed(2)}% of cost`,
      deltaTone: totalGainPercent >= 0 ? "positive" : "negative",
      sparklineData: netWorthSpark,
      sparklineTone: totalGain >= 0 ? "positive" : "negative",
    },
  ];

  if (error) {
    return (
      <AppLayout title="Dashboard">
        <Card className="border-destructive/30 bg-destructive/10">
          <CardContent className="py-8">
            <div className="text-center">
              <Heading level={4} className="text-destructive">
                Failed to load portfolio
              </Heading>
              <Text variant="secondary" className="mt-2">
                {error}
              </Text>
              <Button variant="outline" className="mt-4" onClick={handleRefresh}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="Dashboard"
      secondaryActions={
        <>
          {/* Entry point to the Portfolio Story (issue #250). The story is no
              longer guaranteed to be the landing page, so it needs a way in. */}
          <Button variant="outline" size="sm" asChild>
            <Link to="/story">
              <BookOpen className="h-4 w-4 mr-2" />
              Story
            </Link>
          </Button>
          <Button variant="outline" size="sm" onClick={() => setExportDialogOpen(true)}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
            {isRefreshing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin motion-reduce:animate-none" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3.5 sm:gap-[18px]">
        {/* Portfolio overview / last-updated line (kept for E2E V2-DASH-01) */}
        <Text variant="secondary" size="sm">
          Portfolio overview
          {lastUpdated && (
            <span className="ml-2 text-muted-foreground">
              · Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </Text>

        <ExportDialog open={exportDialogOpen} onOpenChange={setExportDialogOpen} />

        {/* KPI band — 4 sparkline stat-cards */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {kpis.map((kpi) => (
            <StatCard
              key={kpi.label}
              label={kpi.label}
              value={loading ? <Skeleton className="h-[26px] w-24" /> : kpi.value}
              delta={loading ? undefined : kpi.delta}
              deltaTone={kpi.deltaTone}
              sparklineData={loading ? null : kpi.sparklineData}
              sparklineTone={kpi.sparklineTone}
            />
          ))}
        </div>

        {/* Performance vs benchmarks (3fr) + Allocation/Cash-flow-YTD stack (1fr) */}
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[3fr_1fr]">
          <PerformanceVsBenchmarksPanel benchmarkData={benchmarkData} range={range} onRangeChange={setRange} />
          <div className="flex flex-col gap-3">
            <AllocationPanel summary={summary} loading={loading} />
            <CashFlowYtdPanel />
          </div>
        </div>

        {/* Largest positions (3fr) + Daily insights digest (1fr) */}
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[3fr_1fr]">
          <LargestPositionsTable holdings={holdings} loading={loading} />
          <DailyInsightsPanel />
        </div>

        {/* Snapshot history strip */}
        <SnapshotHistoryStrip snapshotHistory={snapshotHistory} range={range} />

        {/* Time in Market insight — not in mockup but retained (D5): no
            functionality may be dropped by a restyle-only issue. */}
        <HoldingAgeInsight holdings={holdings} loading={loading} />
      </div>
    </AppLayout>
  );
}

export default Dashboard;
